import type { TemplateResult } from 'lit'
import type Popout from '../popout/Popout.js'
import { ContextConsumer } from '@lit/context'
import { html, LitElement, nothing } from 'lit'
import { customElement, property, query, state } from 'lit/decorators.js'
import { classMap } from 'lit/directives/class-map.js'
import { ifDefined } from 'lit/directives/if-defined.js'
import { ref } from 'lit/directives/ref.js'
import { DirectionController } from '../common/controllers/DirectionController.js'
import { SlotController } from '../common/controllers/SlotController.js'
import { observe } from '../common/decorators/observe.js'
import { cond } from '../common/directives/cond.js'
import { NordEvent } from '../common/events.js'

import { FocusableMixin } from '../common/mixins/FocusableMixin.js'
import componentStyle from '../common/styles/Component.css'
import { navListContext } from '../nav-group/nav-context.js'
import style from './NavItem.css'

import '../popout/Popout.js'
import '../tooltip/Tooltip.js'

let railTooltipIdCounter = 0
let railPopoutIdCounter = 0

// Mirrors the layout's wide-viewport breakpoint so nav items only
// switch to icon-only rendering when the layout actually shows the rail.
const railMediaQuery
  = typeof matchMedia !== 'undefined'
    ? matchMedia('(min-width: 768px)')
    : ({
        matches: false,
        addEventListener() {
          /* noop */
        },
        removeEventListener() {
          /* noop */
        },
      } as unknown as MediaQueryList)

/**
 * Navigation item populates sidebar navigation with links.
 * Every item should be placed inside a navigation group.
 *
 * @status ready
 * @category navigation
 * @slot - The default slot used for the nav item's text.
 * @slot icon - Slot for a custom leading icon element. Use when the `icon` attribute isn't enough — for example, an icon wrapped in a styled badge or circular frame. When set, it takes priority over the `icon` attribute and is shown in both the expanded nav and the collapsed rail.
 * @slot end - Trailing content rendered after the label inside the item's flex row. The slotted element flows naturally — it claims its real width and the label flex-shrinks to make room. Use for a custom badge, count pill, action icon, etc. when the `badge` attribute's plain text isn't enough.
 * @slot subnav - Used for nesting navigation. When used the nav-item becomes a button to collapse the subnav, rather than a link.
 * @slot rail-tooltip - Full content of the rail-mode tooltip. When provided, it replaces the default label projection — write the tooltip's content directly (label + count + keyboard shortcut etc., bound to whatever reactive state you like). When absent, the tooltip falls back to the item's default-slot label. Only rendered in rail mode.
 *
 * @fires toggle - Dispatched whenever a nav item's state changes between open and closed.
 * @fires activate - Dispatched whenever a nav item has been marked as active
 *
 * @cssprop [--n-nav-item-color=var(--n-color-text-weak)] - Text color in the default state.
 * @cssprop [--n-nav-item-background=transparent] - Background color in the default state.
 * @cssprop [--n-nav-item-border-radius=var(--n-border-radius-s)] - Border radius of the item and its active-parent overlay.
 * @cssprop [--n-nav-item-padding-block=var(--n-space-s)] - Block-axis (top/bottom) padding.
 * @cssprop [--n-nav-item-padding-inline=var(--n-space-s)] - Inline-axis (start/end) padding.
 * @cssprop [--n-nav-item-line-height=var(--n-line-height-tight)] - Line height of the item's text.
 * @cssprop [--n-nav-item-icon-gap=calc(var(--n-space-s) * 1.4)] - Inline gap between the leading icon (the `icon` attribute or `icon` slot) and the item's text.
 * @cssprop [--n-nav-item-color-hover=var(--n-color-text)] - Text color on hover.
 * @cssprop [--n-nav-item-background-hover=var(--n-color-nav-hover)] - Background color on hover.
 * @cssprop [--n-nav-item-color-active=var(--n-color-text-on-accent)] - Text color when the item is `active`.
 * @cssprop [--n-nav-item-background-active=var(--n-color-accent)] - Background color when the item is `active`.
 * @cssprop [--n-nav-item-color-expanded=var(--n-color-text-weak)] - Text color when the item is an active parent (its subnav is expanded).
 * @cssprop [--n-nav-item-background-expanded=var(--n-color-accent)] - Background color of the overlay drawn behind an active-parent item.
 * @cssprop [--n-nav-item-expanded-overlay-opacity=0.12] - Opacity of the active-parent overlay. Set to `1` for a solid background.
 * @cssprop [--n-nav-item-expanded-overlay-filter=brightness(150%)] - Filter applied to the active-parent overlay. Set to `none` to disable.
 * @cssprop [--n-rail-flyout-background=var(--n-color-surface)] - Background colour of the rail-mode subnav flyout container.
 * @cssprop [--n-rail-flyout-box-shadow=var(--n-box-shadow-popout)] - Box-shadow of the rail-mode subnav flyout container.
 * @cssprop [--n-rail-flyout-min-inline-size=220px] - Minimum inline size (width in LTR) of the rail-mode subnav flyout body.
 * @cssprop [--n-rail-flyout-padding=var(--n-space-s)] - Padding of the rail-mode subnav flyout body.
 * @cssprop [--n-rail-flyout-offset-inline=0] - Extra inline-axis offset applied to the rail flyout (positive values push it away from the rail in LTR; mirrored in RTL).
 * @cssprop [--n-rail-flyout-offset-block=0] - Extra block-axis offset applied to the rail flyout (positive values push it downward).
 * @cssprop [--n-rail-subnav-indicator-color=var(--n-color-border-strong)] - Colour of the small triangle indicator drawn in the bottom-inline-end corner of rail items that have a subnav (default + hover state).
 * @cssprop [--n-rail-subnav-indicator-color-active=var(--n-color-border-hover)] - Colour of the triangle indicator while the item's flyout popout is open.
 * @cssprop [--n-nav-badge-background=var(--n-color-status-notification)] - Background colour of the built-in `badge` attribute pill (`.n-nav-badge`).
 * @cssprop [--n-nav-badge-color=#fff] - Text colour of the built-in `badge` attribute pill.
 * @cssprop [--n-nav-badge-font-size=var(--n-font-size-xs)] - Font size of the built-in `badge` attribute pill.
 * @cssprop [--n-nav-badge-font-weight=var(--n-font-weight)] - Font weight of the built-in `badge` attribute pill.
 * @cssprop [--n-nav-badge-line-height=1] - Line height of the built-in `badge` attribute pill. Combined with `--n-nav-badge-padding-block` this controls the pill's effective block size.
 * @cssprop [--n-nav-badge-padding-block=var(--n-space-xs)] - Block-axis padding of the built-in `badge` attribute pill.
 * @cssprop [--n-nav-badge-padding-inline=calc(var(--n-space-s) / 2)] - Inline-axis padding of the built-in `badge` attribute pill.
 * @cssprop [--n-nav-badge-border-radius=var(--n-border-radius-pill)] - Border radius of the built-in `badge` attribute pill.
 * @cssprop [--n-nav-badge-min-inline-size=20px] - Minimum inline size (width in LTR) of the built-in `badge` attribute pill — sets a visual floor for single-digit counts.
 * @cssprop [--n-nav-badge-margin-inline-start=var(--n-space-xs)] - Inline-start margin separating the badge from the preceding label inside the nav-item's flex row.
 * @cssprop [--n-rail-badge-size=5px] - Diameter of the rail-mode dot the badge collapses into when the layout is in `collapse-mode="rail"` and not `nav-open`.
 *
 * @parent Navigation
 */
@customElement('nord-nav-item')
export default class NavItem extends FocusableMixin(LitElement) {
  static styles = [componentStyle, style]

  private subnavSlot = new SlotController(this, 'subnav')
  private iconSlot = new SlotController(this, 'icon')
  private railTooltipSlot = new SlotController(this, 'rail-tooltip')
  private direction = new DirectionController(this)

  private layoutObserver?: MutationObserver
  private observedLayout?: HTMLElement | null
  private readonly tooltipId = `n-nav-rail-tip-${++railTooltipIdCounter}`
  private readonly popoutId = `n-nav-rail-pop-${++railPopoutIdCounter}`

  @query('nord-popout') private popoutEl?: Popout

  /**
   * Reflects whether the ancestor layout is in rail-collapsed state
   * (i.e. `collapse-mode="rail"` and `nav-open` is absent).
   * @internal
   */
  @state() private railCollapsed = false

  /**
   * Whether this item is inside a `nord-nav-group` (which exposes `role="list"`).
   * Only then may it emit `role="listitem"` — a listitem without a list ancestor
   * fails the `aria-required-parent` accessibility check. Standalone items (e.g.
   * directly in `nord-navigation` or used as a `nord-dropdown` toggle) omit the role.
   * @internal
   */
  @state() private inList = false

  private listConsumer = new ContextConsumer(this, {
    context: navListContext,
    subscribe: true,
    callback: (value) => {
      this.inList = value === true
    },
  })

  /**
   * Used for indicating the current page. This gives a prominent background to the nav item,
   * and marks the item as the current page for assistive technology.
   */
  @property({ type: Boolean, reflect: true }) active = false

  /**
   * The name of an icon from Nordicons to display for the nav item.
   */
  @property({ reflect: true }) icon?: string

  /**
   * The url the nav item should link to.
   * Note: this is not used if you have nested navigation using the "subnav" slot.
   */
  @property({ reflect: true }) href?: string

  /**
   * Allows you to add a notification badge with a number next to the nav item.
   */
  @property({ reflect: true }) badge?: string

  /**
   * When the nav items contains a subnav, controls whether the section is expanded or not.
   * Note: this is only used if you have nested navigation using the "subnav" slot.
   */
  @property({ reflect: true, type: Boolean }) open = false

  /**
   * Opt-in: when set, the item's rail-mode subnav flyout closes automatically
   * once one of its leaf descendants is activated (for example, when a user
   * clicks a link inside the flyout). Off by default — existing flyout
   * behavior is unchanged unless this attribute is present.
   */
  @property({ reflect: true, type: Boolean, attribute: 'auto-dismiss-on-select' }) autoDismissOnSelect = false

  /**
   * Distance in pixels between the rail-mode tooltip and the item, forwarded
   * to the internal [Tooltip](/components/tooltip/)'s `side-offset` (the
   * Floating UI `offset` main axis). When unset, the tooltip's own default
   * (`8`) applies. Useful for lining up rail-item tooltips with neighbouring
   * standalone tooltips.
   */
  @property({ reflect: true, type: Number, attribute: 'side-offset' }) sideOffset?: number

  /**
   * Offset in pixels along the alignment axis for the rail-mode tooltip,
   * forwarded to the internal [Tooltip](/components/tooltip/)'s
   * `align-offset`. When unset, the tooltip's own default (`0`) applies.
   */
  @property({ reflect: true, type: Number, attribute: 'align-offset' }) alignOffset?: number

  /**
   * @internal
   */
  get hasSubNav() {
    return this.subnavSlot.hasContent
  }

  /**
   * Closes this item's rail-mode subnav flyout if it is open.
   *
   * In rail-collapsed mode a parent nav item opens its subnav inside a
   * flyout popout. Clicking a leaf link inside that flyout does not close it,
   * because the click passes through the popout host and is treated as
   * "inside" by the popout's light-dismiss logic. Call this method to close
   * the flyout without reaching into the component's shadow DOM.
   *
   * Returns a promise that resolves once the flyout's hide animation ends.
   * If there is no flyout, or it is already closed, the promise resolves
   * immediately.
   */
  async closeFlyout(): Promise<void> {
    await this.popoutEl?.hide()
  }

  connectedCallback() {
    super.connectedCallback()
    this.startObservingLayout()
    railMediaQuery.addEventListener('change', this.handleViewportChange)
    this.addEventListener('activate', this.handleDescendantActivate)
  }

  disconnectedCallback() {
    super.disconnectedCallback()
    this.stopObservingLayout()
    railMediaQuery.removeEventListener('change', this.handleViewportChange)
    this.removeEventListener('activate', this.handleDescendantActivate)
  }

  // When `auto-dismiss-on-select` is set, close this item's rail flyout once a
  // leaf descendant inside it is activated. Ignores the item's own activate
  // event (a parent with a subnav does not fire `activate`, but guard anyway).
  private handleDescendantActivate = (event: Event) => {
    if (!this.autoDismissOnSelect || event.target === this) {
      return
    }
    void this.closeFlyout()
  }

  private handleViewportChange = () => this.updateRailState()

  private startObservingLayout() {
    this.observedLayout = this.closest<HTMLElement>('nord-layout')
    if (!this.observedLayout) {
      return
    }
    this.updateRailState()
    this.layoutObserver = new MutationObserver(() => this.updateRailState())
    this.layoutObserver.observe(this.observedLayout, {
      attributes: true,
      attributeFilter: ['nav-open', 'collapse-mode'],
    })
  }

  private stopObservingLayout() {
    this.layoutObserver?.disconnect()
    this.layoutObserver = undefined
    this.observedLayout = undefined
    this.railCollapsed = false
    this.popoutEl?.hide()
  }

  private updateRailState() {
    if (!this.observedLayout) {
      return
    }
    // Subnav children render inside a flyout popover when rail-collapsed;
    // they should keep their full layout, not collapse to icons themselves.
    const isNested = !!this.parentElement?.closest('nord-nav-item')
    const next = !isNested
      && railMediaQuery.matches
      && this.observedLayout.getAttribute('collapse-mode') === 'rail'
      && !this.observedLayout.hasAttribute('nav-open')
    if (!next) {
      this.popoutEl?.hide()
    }
    this.railCollapsed = next
  }

  render() {
    if (this.railCollapsed) {
      return this.renderRail()
    }

    const innards = html`
      ${this.renderLeadingIcon()}
      <div class="n-nav-content">
        <span class="n-nav-label"><slot></slot></span>
        ${this.badge ? html`<span class="n-nav-badge">${this.badge}</span>` : nothing}
        <slot name="end"></slot>
      </div>
    `
    let element: TemplateResult

    if (this.subnavSlot.hasContent) {
      element = this.renderToggle(innards)
    }
    else if (this.href) {
      element = this.renderLink(innards)
    }
    else {
      element = this.renderButton(innards)
    }

    return html`
      <div role=${cond(this.inList, 'listitem')}>
        ${element}
        <slot name=${this.subnavSlot.slotName} ?hidden=${!this.open}></slot>
      </div>
    `
  }

  // Resolves the leading icon shown in both the expanded nav and the rail:
  // a slotted custom icon wins over the `icon` attribute, which wins over nothing.
  private renderLeadingIcon(): TemplateResult | typeof nothing {
    if (this.iconSlot.hasContent) {
      return html`<slot name="icon" class="n-nav-icon"></slot>`
    }
    if (this.icon) {
      return html`<nord-icon class="n-nav-icon" name=${this.icon} size="m"></nord-icon>`
    }
    return nothing
  }

  private renderRail(): TemplateResult {
    const leadingIcon = this.renderLeadingIcon()
    // Rail is icon-only, so when there's no icon at all we fall back to the
    // first letter of the label rather than rendering an empty button.
    const iconEl = leadingIcon === nothing
      ? html`<span class="n-rail-fallback">${this.textInitial}</span>`
      : leadingIcon
    const badge = this.badge ? html`<span class="n-nav-badge n-rail-badge">${this.badge}</span>` : nothing

    if (this.subnavSlot.hasContent) {
      return this.renderRailFlyout(iconEl, badge)
    }

    return html`
      <div role=${cond(this.inList, 'listitem')} class="n-rail-host">
        ${this.href
          ? html`
              <a
                class="n-nav-item n-rail-item"
                ${ref(this.focusableRef)}
                aria-current=${cond(this.active, 'page')}
                aria-describedby=${this.tooltipId}
                href=${this.href}
              >
                ${iconEl}${badge}
              </a>
            `
          : html`
              <button
                class="n-nav-item n-rail-item"
                ${ref(this.focusableRef)}
                aria-describedby=${this.tooltipId}
              >
                ${iconEl}${badge}
              </button>
            `}
        <slot name="end" class="n-rail-slotted-end"></slot>
        <nord-tooltip id=${this.tooltipId} position="inline-end" side-offset=${ifDefined(this.sideOffset)} align-offset=${ifDefined(this.alignOffset)}>
          ${this.railTooltipSlot.hasContent
            ? html`<slot name="rail-tooltip"></slot>`
            : html`<slot></slot>`}
        </nord-tooltip>
      </div>
    `
  }

  private renderRailFlyout(iconEl: TemplateResult, badge: TemplateResult | typeof nothing): TemplateResult {
    // Click-only: nord-popout's getToggle() listens for click on the
    // [aria-controls={popoutId}] target. Hover used to open as well, but
    // that conflicted with the popout's click-to-toggle (hover opened, then
    // the click was treated as a close).
    // The tooltip still shows on hover so the icon has an accessible label
    // — nord-tooltip listens for mouseover/focusin on [aria-describedby].
    return html`
      <div role=${cond(this.inList, 'listitem')} class="n-rail-host">
        <button
          class="n-nav-item n-rail-item"
          ${ref(this.focusableRef)}
          aria-controls=${this.popoutId}
          aria-describedby=${this.tooltipId}
        >
          ${iconEl}${badge}
          <svg
            class="n-rail-subnav-indicator"
            aria-hidden="true"
            xmlns="http://www.w3.org/2000/svg"
            width="5"
            height="5"
            viewBox="0 0 5 5"
            fill="none"
          >
            <path
              d="M4.29388 3.79388V0.500991C4.29388 0.0555387 3.75531 -0.167545 3.44033 0.147438L0.147437 3.44033C-0.167545 3.75531 0.0555386 4.29388 0.500991 4.29388H3.79388C4.07003 4.29388 4.29388 4.07003 4.29388 3.79388Z"
              fill="currentColor"
            />
          </svg>
        </button>
        <slot name="end" class="n-rail-slotted-end"></slot>
        <nord-tooltip id=${this.tooltipId} position="inline-end" side-offset=${ifDefined(this.sideOffset)} align-offset=${ifDefined(this.alignOffset)}>
          ${this.railTooltipSlot.hasContent
            ? html`<slot name="rail-tooltip"></slot>`
            : html`<slot></slot>`}
        </nord-tooltip>
        <nord-popout id=${this.popoutId} position="inline-end" always-floating>
          <div class="n-rail-flyout-body">
            <slot name=${this.subnavSlot.slotName}></slot>
          </div>
        </nord-popout>
      </div>
    `
  }

  private get textInitial(): string {
    const text = this.textContent?.trim() ?? ''
    return text.charAt(0).toUpperCase()
  }

  private renderLink(innards: TemplateResult) {
    return html`
      <a class="n-nav-item" ${ref(this.focusableRef)} aria-current=${cond(this.active, 'page')} href=${this.href || ''}>
        ${innards}
      </a>
    `
  }

  private renderToggle(innards: TemplateResult) {
    return html`
      <button
        class="n-nav-item"
        @click=${this.toggleOpen}
        aria-expanded=${this.open ? 'true' : 'false'}
        ${ref(this.focusableRef)}
      >
        ${innards}

        <nord-icon
          size="xxs"
          class=${classMap({ 'n-toggle-icon': true, 'n-rtl': this.direction.isRTL })}
          name="arrow-expand-right-small"
        ></nord-icon>
      </button>
    `
  }

  private renderButton(innards: TemplateResult) {
    return html`<button class="n-nav-item" ${ref(this.focusableRef)}>${innards}</button>`
  }

  private toggleOpen() {
    this.open = !this.open
    this.dispatchEvent(new NordEvent('toggle'))
  }

  @observe('active', 'updated')
  protected handleActiveChange() {
    if (this.active && !this.hasSubNav) {
      this.dispatchEvent(new NordEvent('activate'))
    }
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'nord-nav-item': NavItem
  }
}
